import { supabase, supabaseAdmin } from "../../config/supabaseClient";
import {
  analyticsLocalDayIsoRange,
  compareAnalyticsFormattedDates,
  rpcTemplateAnalytics,
} from "../analytics/analyticsDateRange";
import { templateRouteName } from "./templateAnalyticsEvents";

const COMBINED_CONTENT_KEYS = ["content", "platform"] as const;
const COMBINED_JOURNAL_KEYS = ["journal", "journalPost"] as const;

function formatShopAnalyticsSubtitle(
  details: Record<string, unknown>,
): string {
  const parts = [
    details.category_primary,
    details.category_secondary,
    details.category_brand,
    details.content_category,
  ]
    .map((v) => String(v || "").trim())
    .filter(Boolean);
  return parts.join(" · ");
}

function formatContentAnalyticsSubtitle(
  details: Record<string, unknown>,
): string {
  const view = String(details.content_view || "").trim().toLowerCase();
  const tab = String(details.content_tab || "").trim();
  if (view === "series") {
    return tab ? `From Series · ${tab}` : "From Series";
  }
  if (view === "latest") {
    return tab ? `From Latest · ${tab}` : "From Latest";
  }
  return tab ? `From ${tab}` : "";
}

function routeFor(username: string): string {
  return templateRouteName(username);
}

/** Single page_key filter for RPCs (`overview` / null = site-wide). */
function pageFilter(pageKey?: string | null): string | null {
  if (!pageKey || pageKey === "overview") return null;
  return pageKey;
}

function isCombinedContentTab(pageKey?: string | null): boolean {
  return pageKey === "content";
}

function isCombinedJournalTab(pageKey?: string | null): boolean {
  return pageKey === "journal";
}

function combinedPageKeys(pageKey?: string | null): readonly string[] | null {
  if (isCombinedContentTab(pageKey)) return COMBINED_CONTENT_KEYS;
  if (isCombinedJournalTab(pageKey)) return COMBINED_JOURNAL_KEYS;
  return null;
}

function baseRpc(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  filterPageKey: string | null,
) {
  // Same args as deployed RPCs — do not add params (would break PostgREST).
  return {
    route_name: routeFor(username),
    start_date: startDate,
    end_date: endDate,
    userid: user_id,
    filter_page_key: filterPageKey,
  };
}

type OverviewRow = {
  formatted_date: string;
  users: number;
  searches: number;
  links: number;
  page_views: number;
};

function mergeOverviewSeries(parts: OverviewRow[][]): OverviewRow[] {
  const map = new Map<string, OverviewRow>();
  for (const series of parts) {
    for (const row of series) {
      const key = String(row.formatted_date);
      const cur = map.get(key) || {
        formatted_date: row.formatted_date,
        users: 0,
        searches: 0,
        links: 0,
        page_views: 0,
      };
      cur.users += Number(row.users || 0);
      cur.searches += Number(row.searches || 0);
      cur.links += Number(row.links || 0);
      cur.page_views += Number(row.page_views || 0);
      map.set(key, cur);
    }
  }
  return [...map.values()].sort((a, b) =>
    compareAnalyticsFormattedDates(a.formatted_date, b.formatted_date),
  );
}

async function fetchOverviewForFilter(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  filterPageKey: string | null,
): Promise<OverviewRow[]> {
  const { data } = await rpcTemplateAnalytics(
    "get_template_overview_views_date_range",
    baseRpc(username, startDate, endDate, user_id, filterPageKey),
  );
  return (data as OverviewRow[]) ?? [];
}

export async function getTemplateOverviewAnalytics(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  let rows: OverviewRow[];
  const mergedKeys = combinedPageKeys(pageKey);
  if (mergedKeys) {
    const parts = await Promise.all(
      mergedKeys.map((key) =>
        fetchOverviewForFilter(username, startDate, endDate, user_id, key),
      ),
    );
    rows = mergeOverviewSeries(parts);
  } else {
    rows = await fetchOverviewForFilter(
      username,
      startDate,
      endDate,
      user_id,
      pageFilter(pageKey),
    );
  }
  if (!rows.length) return [];
  return [...rows].sort((a, b) =>
    compareAnalyticsFormattedDates(a.formatted_date, b.formatted_date),
  );
}

async function fetchUniqueUsersForFilter(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  filterPageKey: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_unique_users_date_range",
    baseRpc(username, startDate, endDate, user_id, filterPageKey),
  );
  return data?.[0]?.count ?? 0;
}

/** Distinct-ish unique visitors across Recent + Latest Content pages. */
async function fetchUniqueUsersForPages(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKeys: readonly string[],
) {
  // Prefer per-page RPCs (same as other tabs). Sum may slightly over-count
  // visitors who hit both pages; avoids empty direct-table reads returning 0.
  const parts = await Promise.all(
    pageKeys.map((key) =>
      fetchUniqueUsersForFilter(username, startDate, endDate, user_id, key),
    ),
  );
  return parts.reduce((sum, n) => sum + Number(n || 0), 0);
}

export async function getTemplateUniqueUsers(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const mergedKeys = combinedPageKeys(pageKey);
  if (mergedKeys) {
    return fetchUniqueUsersForPages(
      username,
      startDate,
      endDate,
      user_id,
      mergedKeys,
    );
  }
  return fetchUniqueUsersForFilter(
    username,
    startDate,
    endDate,
    user_id,
    pageFilter(pageKey),
  );
}

async function fetchPageViewsForFilter(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  filterPageKey: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_page_views_date_range",
    baseRpc(username, startDate, endDate, user_id, filterPageKey),
  );
  return data?.[0]?.count ?? 0;
}

export async function getTemplatePageViewsTotal(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const mergedKeys = combinedPageKeys(pageKey);
  if (mergedKeys) {
    const parts = await Promise.all(
      mergedKeys.map((key) =>
        fetchPageViewsForFilter(username, startDate, endDate, user_id, key),
      ),
    );
    return parts.reduce((sum, n) => sum + Number(n || 0), 0);
  }
  return fetchPageViewsForFilter(
    username,
    startDate,
    endDate,
    user_id,
    pageFilter(pageKey),
  );
}

export async function getTemplatePageBreakdown(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
) {
  const { data } = await rpcTemplateAnalytics("get_template_page_breakdown", {
    route_name: routeFor(username),
    start_date: startDate,
    end_date: endDate,
    userid: user_id,
  });
  return data ?? [];
}

export async function getTemplateNavClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_nav_clicks",
    baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
  );
  return data ?? [];
}

export async function getTemplateCarouselTabClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_carousel_tab_clicks",
    baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
  );
  return data ?? [];
}

export async function getTemplateSectionClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_section_clicks",
    baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
  );
  return data ?? [];
}

const FEATURED_SECTION_TYPES = [
  "FeaturedCategories",
  "Featured Category",
  "FeaturedCollections",
  "Featured Collections",
  "Featured Outfit",
  "Recent Looks",
  "Content Looks",
];

function featuredAnalyticsType(sectionType: string): string {
  const raw = String(sectionType || "").trim();
  if (raw === "FeaturedCategories" || raw === "Featured Category") {
    return "Featured Category";
  }
  if (raw === "FeaturedCollections" || raw === "Featured Collections") {
    return "Featured Collections";
  }
  if (raw === "Featured Outfit") return "Featured Outfit";
  if (raw === "Recent Looks" || raw === "Content Looks") {
    return "Recent Looks";
  }
  return "";
}

function featuredDetails(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === "object") return raw as Record<string, unknown>;
  if (typeof raw === "string") {
    try {
      const parsed = JSON.parse(raw);
      return parsed && typeof parsed === "object" ? parsed : {};
    } catch {
      return {};
    }
  }
  return {};
}

function featuredMedia(value: unknown): string {
  if (typeof value !== "string") return "";
  const first = value.split(",").map((part) => part.trim()).find(Boolean) || "";
  if (!first || first.startsWith("blob:")) return "";
  return first;
}

function featuredPostName(label: string, details: Record<string, unknown>): string {
  for (const key of ["name", "title", "product_name", "caption"]) {
    const value = String(details[key] || "").trim();
    if (value) return value;
  }
  const raw = String(label || "").trim();
  const shop = raw.match(/^Shop\s*·\s*(.+)$/i);
  return (shop?.[1] || raw || "Untitled").trim();
}

export type TemplateFeaturedClickRow = {
  id: string;
  name: string;
  image: string;
  featureType: string;
  clicks: number;
};

export async function getTemplateFeaturedClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
): Promise<TemplateFeaturedClickRow[]> {
  let query = supabaseAdmin
    .from("template_analytics")
    .select("section_type, entity_id, entity_label, details")
    .eq("user_id", user_id)
    .eq("type", "event")
    .in("name", ["Section Click", "Featured Carousel Click"])
    .in("section_type", FEATURED_SECTION_TYPES)
    .gte("created_at", `${startDate}T00:00:00.000Z`)
    .lte("created_at", `${endDate}T23:59:59.999Z`)
    .limit(5000);
  const filter = pageFilter(pageKey);
  if (filter) query = query.eq("page_key", filter);
  const { data } = await query;

  const grouped = new Map<
    string,
    { id: string; name: string; image: string; featureType: string; clicks: number }
  >();
  for (const row of data || []) {
    const featureType = featuredAnalyticsType(String(row.section_type || ""));
    if (!featureType) continue;
    const details = featuredDetails(row.details);
    const id = String(row.entity_id || details.post_id || "").trim();
    const name = featuredPostName(String(row.entity_label || ""), details);
    const key = `${featureType}|${id || name}`;
    const image =
      featuredMedia(details.image) ||
      featuredMedia(details.media_url) ||
      featuredMedia(details.thumbnail);
    const cur = grouped.get(key);
    if (cur) {
      cur.clicks += 1;
      if (!cur.image && image) cur.image = image;
      if (cur.name === "Untitled" && name) cur.name = name;
    } else {
      grouped.set(key, { id, name, image, featureType, clicks: 1 });
    }
  }

  const rows = [...grouped.values()];
  const missing = rows.filter((row) => row.id && !row.image).map((row) => row.id);
  if (missing.length && username) {
    const { data: postRows } = await supabase
      .from(username)
      .select("*")
      .in("post_id", Array.from(new Set(missing)))
      .eq("user_id", user_id);
    const posts = new Map<string, Record<string, unknown>>();
    for (const post of postRows || []) {
      const id = String((post as { post_id?: string }).post_id || "").trim();
      if (id) posts.set(id, post as Record<string, unknown>);
    }
    for (const row of rows) {
      const post = row.id ? posts.get(row.id) : undefined;
      if (!post) continue;
      if (!row.image) {
        row.image =
          featuredMedia(post.thumbnail) ||
          featuredMedia(post.media_url) ||
          featuredMedia(post.image);
      }
      if (!row.name || row.name === "Untitled") {
        row.name = featuredPostName("", post);
      }
    }
  }

  return rows.sort((a, b) => b.clicks - a.clicks);
}

export type TemplateCarouselItemClick = {
  carousel: string;
  item: string;
  itemType: string;
  clicks: number;
};

export async function getTemplateCarouselItemClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
): Promise<TemplateCarouselItemClick[]> {
  void username;
  let query = supabaseAdmin
    .from("template_analytics")
    .select("section_id, entity_id, entity_type, entity_label, details")
    .eq("user_id", user_id)
    .eq("type", "event")
    .in("name", ["Featured Carousel Click", "Carousel Item Click"])
    .eq("section_type", "Carousel")
    .gte("created_at", `${startDate}T00:00:00.000Z`)
    .lte("created_at", `${endDate}T23:59:59.999Z`)
    .limit(5000);
  const filter = pageFilter(pageKey);
  if (filter) query = query.eq("page_key", filter);
  const { data } = await query;

  const rows = data || [];
  const titleBySection = new Map<string, string>();
  for (const row of rows) {
    const details = featuredDetails(row.details);
    const sectionId = String(row.section_id || "").trim();
    const titled = String(details.carousel_title || "").trim();
    const label = String(row.entity_label || "").trim();
    const title =
      titled ||
      (String(details.scope || "") === "section" ? label : "") ||
      (String(row.entity_type || "") === "carousel" && !details.kind && !details.name
        ? label
        : "");
    if (sectionId && title) titleBySection.set(sectionId, title);
  }
  const grouped = new Map<string, TemplateCarouselItemClick>();
  for (const row of rows) {
    const details = featuredDetails(row.details);
    if (String(details.scope || "") === "section") continue;
    const sectionId = String(row.section_id || "").trim();
    const carousel =
      String(details.carousel_title || "").trim() ||
      titleBySection.get(sectionId) ||
      "";
    if (!carousel) continue;
    const item = String(details.name || row.entity_label || "").trim() || "Item";
    if (item === carousel && !details.name) continue;
    const itemType = String(details.platform || details.kind || "").trim() || "—";
    const key = `${carousel}|${String(row.entity_id || item)}|${itemType}`;
    const cur = grouped.get(key);
    if (cur) cur.clicks += 1;
    else grouped.set(key, { carousel, item, itemType, clicks: 1 });
  }
  return [...grouped.values()].sort((a, b) => b.clicks - a.clicks);
}

async function enrichPostClicks(
  username: string,
  user_id: string,
  data: Array<{ details: string; count: number }>,
) {
  if (!Array.isArray(data) || !data.length) return [];

  const parsed = data
    .map((item) => {
      try {
        const parsedDetails = JSON.parse(item.details || "{}") as Record<
          string,
          unknown
        >;
        const post_id = String(parsedDetails.post_id ?? "").trim();
        if (!post_id) return null;
        return { post_id, count: item.count, parsedDetails };
      } catch {
        return null;
      }
    })
    .filter(Boolean) as Array<{
    post_id: string;
    count: number;
    parsedDetails: Record<string, unknown>;
  }>;

  if (!parsed.length) return [];

  const postIds = Array.from(new Set(parsed.map((p) => p.post_id)));
  const { data: postRows } = await supabase
    .from(username)
    .select("*")
    .in("post_id", postIds)
    .eq("user_id", user_id);

  const postMap = new Map<string, Record<string, unknown>>();
  for (const row of postRows || []) {
    const id = String((row as { post_id?: string }).post_id ?? "").trim();
    if (id && !postMap.has(id)) postMap.set(id, row as Record<string, unknown>);
  }

  const unresolved = postIds.filter((id) => !postMap.has(id)).slice(0, 25);
  if (unresolved.length) {
    await Promise.all(
      unresolved.map(async (id) => {
        const { data: mediaRows } = await supabase
          .from(username)
          .select("*")
          .ilike("media_url", `%${id}%`)
          .limit(1);
        if (mediaRows?.[0]) {
          postMap.set(id, mediaRows[0] as Record<string, unknown>);
        }
      }),
    );
  }

  return parsed.map((p) => {
    const dbRow = postMap.get(p.post_id);
    return {
      post_id: p.post_id,
      count: p.count,
      additionalData: { ...p.parsedDetails, ...(dbRow || {}) },
      analyticsSubtitle: formatContentAnalyticsSubtitle(p.parsedDetails),
    };
  });
}

async function fetchPostClicksRaw(
  username: string,
  max: number,
  startDate: string,
  endDate: string,
  user_id: string,
  filterPageKey: string | null,
) {
  const { data } = await rpcTemplateAnalytics("get_template_posts_date_range", {
    ...baseRpc(username, startDate, endDate, user_id, filterPageKey),
    max_res: max,
  });
  return (data as Array<{ details: string; count: number }>) ?? [];
}

function mergeCountedDetails(
  parts: Array<Array<{ details: string; count: number }>>,
  max: number,
) {
  const map = new Map<string, { details: string; count: number }>();
  for (const rows of parts) {
    for (const item of rows) {
      let key = item.details;
      try {
        const parsed = JSON.parse(item.details || "{}");
        key = String(parsed.post_id || parsed.entity_id || item.details);
      } catch {
        /* keep details */
      }
      const cur = map.get(key);
      if (cur) {
        cur.count += Number(item.count || 0);
      } else {
        map.set(key, {
          details: item.details,
          count: Number(item.count || 0),
        });
      }
    }
  }
  return [...map.values()]
    .sort((a, b) => b.count - a.count)
    .slice(0, Math.max(max, 1));
}

export async function getTemplatePostClicks(
  username: string,
  max: number,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  let raw: Array<{ details: string; count: number }>;
  if (isCombinedContentTab(pageKey)) {
    const parts = await Promise.all(
      COMBINED_CONTENT_KEYS.map((key) =>
        fetchPostClicksRaw(username, max, startDate, endDate, user_id, key),
      ),
    );
    raw = mergeCountedDetails(parts, max);
  } else {
    raw = await fetchPostClicksRaw(
      username,
      max,
      startDate,
      endDate,
      user_id,
      pageFilter(pageKey),
    );
  }
  return enrichPostClicks(username, user_id, raw);
}

/**
 * Website shop-click post ids, same RPC and site-wide scope as Top shopped.
 * No page filter — Home and Products clicks both count. Sorted by clicks desc.
 */
export async function getTemplateShoppablePostIds(args: {
  username: string;
  userId: string;
  startDate: string;
  endDate: string;
  minClicks?: number | string;
  max?: number;
}): Promise<string[]> {
  const minClicks = Math.max(1, Number(args.minClicks) || 1);
  const max = Math.max(1, Number(args.max) || 5000);
  const { data, error } = await rpcTemplateAnalytics(
    "get_template_shoppable_date_range",
    {
      ...baseRpc(args.username, args.startDate, args.endDate, args.userId, null),
      max_res: max,
    },
  );
  if (error || !Array.isArray(data) || !data.length) return [];

  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of data as Array<{ details: unknown; count: number }>) {
    if (Number(item?.count) < minClicks) continue;
    const postId = postIdFromShopDetails(item?.details);
    if (!postId || seen.has(postId)) continue;
    seen.add(postId);
    ids.push(postId);
  }
  return ids;
}

/**
 * Website content post ids, same RPC as Top viewed (`Post Click`).
 * No page filter — Home and Content clicks both count. Sorted by clicks desc.
 */
export async function getTemplateContentPostIds(args: {
  username: string;
  userId: string;
  startDate: string;
  endDate: string;
  minClicks?: number | string;
  max?: number;
}): Promise<string[]> {
  const minClicks = Math.max(1, Number(args.minClicks) || 1);
  const max = Math.max(1, Number(args.max) || 5000);
  const { data, error } = await rpcTemplateAnalytics(
    "get_template_posts_date_range",
    {
      ...baseRpc(args.username, args.startDate, args.endDate, args.userId, null),
      max_res: max,
    },
  );
  if (error || !Array.isArray(data) || !data.length) return [];

  const ids: string[] = [];
  const seen = new Set<string>();
  for (const item of data as Array<{ details: unknown; count: number }>) {
    if (Number(item?.count) < minClicks) continue;
    const postId = postIdFromShopDetails(item?.details);
    if (!postId || seen.has(postId)) continue;
    seen.add(postId);
    ids.push(postId);
  }
  return ids;
}

function postIdFromShopDetails(details: unknown): string {
  try {
    const parsed =
      typeof details === "string"
        ? (JSON.parse(details || "{}") as Record<string, unknown>)
        : details && typeof details === "object"
          ? (details as Record<string, unknown>)
          : null;
    return String(parsed?.post_id ?? "").trim();
  } catch {
    return "";
  }
}

/**
 * Top shopped / trending shop products.
 * Prefer `pageKey: null` (site-wide): shop clicks are attributed with the
 * origin page_key (search, home, shop, …) but Top shopped should sum them.
 */
export async function getTemplateShoppableClicks(
  username: string,
  max: number,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data, error } = await rpcTemplateAnalytics(
    "get_template_shoppable_date_range",
    {
      ...baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
      max_res: max,
    },
  );
  if (error) throw error;
  if (!Array.isArray(data) || !data.length) return [];

  // Batch enrich — avoid 3 round-trips per click row (was N+1 on home/shop trending).
  const parsed = data
    .map((item: { details: string; count: number }) => {
      try {
        const parsedDetails = JSON.parse(item.details || "{}") as Record<
          string,
          unknown
        >;
        const post_id = String(parsedDetails.post_id ?? "").trim();
        if (!post_id) return null;
        return { post_id, count: item.count, parsedDetails };
      } catch {
        return null;
      }
    })
    .filter(Boolean) as Array<{
    post_id: string;
    count: number;
    parsedDetails: Record<string, unknown>;
  }>;

  if (!parsed.length) return [];

  const postIds = Array.from(new Set(parsed.map((p) => p.post_id)));
  const [{ data: postIdRows }, { data: productRows }] = await Promise.all([
    supabase.from(username).select("*").in("post_id", postIds),
    supabase
      .from("product_bank")
      .select("*")
      .in("product_id", postIds)
      .eq("user_id", user_id),
  ]);

  const postIdMap = new Map<string, Record<string, unknown>>();
  for (const row of postIdRows || []) {
    const id = String((row as { post_id?: string }).post_id ?? "").trim();
    if (id && !postIdMap.has(id)) {
      postIdMap.set(id, row as Record<string, unknown>);
    }
  }

  const productMap = new Map<string, Record<string, unknown>>();
  for (const row of productRows || []) {
    const id = String((row as { product_id?: string }).product_id ?? "").trim();
    if (id && !productMap.has(id)) {
      productMap.set(id, row as Record<string, unknown>);
    }
  }

  const unresolved = postIds.filter((id) => !postIdMap.has(id)).slice(0, 25);
  const mediaMap = new Map<string, Record<string, unknown>>();
  if (unresolved.length) {
    await Promise.all(
      unresolved.map(async (id) => {
        const { data: mediaRows } = await supabase
          .from(username)
          .select("*")
          .ilike("media_url", `%${id}%`)
          .limit(1);
        if (mediaRows?.[0]) {
          mediaMap.set(id, mediaRows[0] as Record<string, unknown>);
        }
      }),
    );
  }

  return parsed.map((p) => {
    const dbRow =
      postIdMap.get(p.post_id) ||
      mediaMap.get(p.post_id) ||
      productMap.get(p.post_id) ||
      null;
    return {
      post_id: p.post_id,
      count: p.count,
      additionalData: { ...p.parsedDetails, ...(dbRow || {}) },
      analyticsSubtitle: formatShopAnalyticsSubtitle(p.parsedDetails),
    };
  });
}

export async function getTemplatePopularSearches(
  username: string,
  max: number,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_popular_searches_date_range",
    {
      ...baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
      max_res: max,
    },
  );
  return data ?? [];
}

type ContentTabRow = {
  entity_label: string;
  content_view: string;
  count: number;
};

function mergeContentTabRows(parts: ContentTabRow[][]): ContentTabRow[] {
  const map = new Map<string, ContentTabRow>();
  for (const rows of parts) {
    for (const row of rows) {
      const key = `${row.entity_label}::${row.content_view}`;
      const cur = map.get(key);
      if (cur) {
        cur.count += Number(row.count || 0);
      } else {
        map.set(key, {
          entity_label: row.entity_label,
          content_view: row.content_view,
          count: Number(row.count || 0),
        });
      }
    }
  }
  return [...map.values()].sort((a, b) => b.count - a.count);
}

export async function getTemplateContentTabClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  if (isCombinedContentTab(pageKey)) {
    const [parts, platformCarouselTabs] = await Promise.all([
      Promise.all(
        COMBINED_CONTENT_KEYS.map(async (key) => {
          const { data, error } = await rpcTemplateAnalytics(
            "get_template_content_tab_clicks",
            baseRpc(username, startDate, endDate, user_id, key),
          );
          if (error) return [] as ContentTabRow[];
          return (data as ContentTabRow[]) ?? [];
        }),
      ),
      // Historical Latest Content pills were tracked as Carousel Tab Click with
      // page_key=platform — fold them into Content tabs so the widget isn't empty.
      rpcTemplateAnalytics(
        "get_template_carousel_tab_clicks",
        baseRpc(username, startDate, endDate, user_id, "platform"),
      ).then(({ data, error }) => {
        if (error) return [] as ContentTabRow[];
        const rows = (data as Array<{
          entity_label?: string;
          carousel_slug?: string;
          count?: number;
        }>) ?? [];
        return rows.map(
          (row): ContentTabRow => ({
            entity_label: String(
              row.entity_label || row.carousel_slug || "unknown",
            ),
            content_view: "latest",
            count: Number(row.count || 0),
          }),
        );
      }),
    ]);
    return mergeContentTabRows([...parts, platformCarouselTabs]);
  }

  const { data, error } = await rpcTemplateAnalytics(
    "get_template_content_tab_clicks",
    baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
  );
  if (error) return [];
  return data ?? [];
}

export async function getTemplateShopTabClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const { data } = await rpcTemplateAnalytics(
    "get_template_shop_tab_clicks",
    baseRpc(username, startDate, endDate, user_id, pageFilter(pageKey)),
  );
  return data ?? [];
}

export async function getTemplateJournalClicks(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  // Opens only — "Journal Post Click". Never include "Journal Post Read"
  // (scroll depth; now stored as type=render). Counting both was ~2× per open.
  // Clicks are always written with page_key=journal (see trackJournalPost).
  const { startIso, endIso } = analyticsLocalDayIsoRange(startDate, endDate);
  let query = supabase
    .from("template_analytics")
    .select("entity_id, entity_label, details")
    .eq("user_id", user_id)
    .eq("route_name", routeFor(username))
    .eq("type", "event")
    .eq("name", "Journal Post Click")
    .gte("created_at", startIso)
    .lte("created_at", endIso)
    .limit(5000);

  const filter = pageFilter(pageKey);
  if (filter === "journal") {
    query = query.eq("page_key", "journal");
  } else if (filter) {
    query = query.eq("page_key", filter);
  }

  const { data } = await query;
  const grouped = new Map<
    string,
    { entity_label: string; entity_id: string; count: number }
  >();
  for (const row of data || []) {
    const details =
      row.details && typeof row.details === "object"
        ? (row.details as Record<string, unknown>)
        : {};
    const entity_id = String(
      row.entity_id || details.postId || details.slug || "",
    ).trim();
    const entity_label = String(
      row.entity_label || details.title || entity_id || "unknown",
    ).trim();
    const key = entity_id || entity_label;
    const cur = grouped.get(key);
    if (cur) cur.count += 1;
    else grouped.set(key, { entity_id, entity_label, count: 1 });
  }
  return [...grouped.values()].sort((a, b) => b.count - a.count);
}

/** Form submissions on the Contact page (`Contact Submit` events). */
export async function getTemplateContactSubmits(
  username: string,
  startDate: string,
  endDate: string,
  user_id: string,
  pageKey?: string | null,
) {
  const route = routeFor(username);
  const { startIso, endIso } = analyticsLocalDayIsoRange(startDate, endDate);

  let query = supabase
    .from("template_analytics")
    .select("id", { count: "exact", head: true })
    .eq("user_id", user_id)
    .eq("route_name", route)
    .eq("name", "Contact Submit")
    .eq("type", "event")
    .gte("created_at", startIso)
    .lte("created_at", endIso);

  const filter = pageFilter(pageKey);
  if (filter) {
    query = query.eq("page_key", filter);
  }

  const { count, error } = await query;
  if (error) return 0;
  return count ?? 0;
}
